import React, { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Flex,
  Spinner,
  Text,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import ImportAlarmExcelModalComponent from '../components/ImportAlarmExcelModalComponent';
import { useQuery, useMutation } from 'react-query';
import {
  GetAllAlarmSettings,
  GetAllDeviceListWithTags,
  GetAlarmGroup,
  getAlarmById,
  createAlarm,
  updateAlarmTag,
  deleteAlarm,
  updateAlarm,
  deleteAlarmGroup,
  updateAlarmGroup,
} from '../api/alarm';
import CreateAlarmModalComponent from '../components/createAlarm.component';
import SelectTagModal from '../components/selectTagModal.component';
import ReferenceInputModal from '../components/referenceInputModal.component';
import DeleteAlarmRulesModalComponent from '../components/deleteAlarmRulesModal.component';
import AddAlarmGroupModalComponent from '../components/addAlarmGroupModal.component';
import { saveAs } from 'file-saver';
import { exportAlarmsToExcel } from '../api/alarm';
import { getApiErrorMessage } from '../utils/apiError';

const Alarms = () => {
  const toast = useToast();
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [selectedTagAlarm, setSelectedTagAlarm] = useState(null);
  const [isFetchingAlarm, setIsFetchingAlarm] = useState(false);
  const [tags, setTags] = useState([]);
  const [alarmGroups, setAlarmGroups] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const [referenceModalLabel, setReferenceModalLabel] = useState('');
  const [selectedReference, setSelectedReference] = useState('');
  const [selectedReferenceAlarmId, setSelectedReferenceAlarmId] =
    useState(null);
  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [refType, setRefType] = useState(0);
  const [operand, setOperand] = useState(''); // Add this line to manage operand state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [newAlarm, setNewAlarm] = useState({
    enable: false,
    name: '',
    group: 'warning',
    operand: 3,
    delay: 'none',
    firstReferance: '',
    secondReferance: '',
    code: '',
    threshold: '',
  });
  const [editGroup, setEditGroup] = useState({
    name: '',
    description: '',
  });

  const {
    data: alarmData,
    error: alarmError,
    isLoading: alarmLoading,
    refetch: refetchAlarms,
  } = useQuery('alarmsKey', GetAllAlarmSettings);

  const {
    data: alarmGroupData,
    error: alarmGroupError,
    isLoading: alarmGroupLoading,
    refetch: refetchAlarmGroups,
  } = useQuery('alarmGroups', GetAlarmGroup);

  const {
    data: tagData,
    error: tagError,
    isLoading: tagLoading,
  } = useQuery('tagsKey', GetAllDeviceListWithTags);

  const mutation = useMutation(createAlarm, {
    onSuccess: () => {
      toast({
        title: 'Alarm created.',
        description: 'The alarm has been created successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsCreateModalOpen(false);
      refetchAlarms();
    },
    onError: error => {
      toast({
        title: 'Error creating alarm.',
        description: getApiErrorMessage(error, 'Failed to create the alarm.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const updateMutation = useMutation(updateAlarm, {
    onSuccess: () => {
      toast({
        title: 'Alarm updated.',
        description: 'The alarm has been updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsEditModalOpen(false);
      refetchAlarms();
    },
    onError: error => {
      toast({
        title: 'Error updating alarm.',
        description: getApiErrorMessage(error, 'Failed to update the alarm.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const deleteMutation = useMutation(deleteAlarm, {
    onSuccess: () => {
      toast({
        title: 'Alarm deleted.',
        description: 'The alarm has been deleted successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsDeleteModalOpen(false);
      setAlarmToDelete(null);
      refetchAlarms();
    },
    onError: error => {
      toast({
        title: 'Error deleting alarm.',
        description: getApiErrorMessage(error, 'Failed to delete the alarm.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const tagMutation = useMutation(updateAlarmTag, {
    onSuccess: () => {
      toast({
        title: 'Tag updated.',
        description: 'The tag has been updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsTagModalOpen(false);
      refetchAlarms();
    },
    onError: error => {
      toast({
        title: 'Error updating tag.',
        description: getApiErrorMessage(error, 'Failed to update the tag.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const deleteGroupMutation = useMutation(deleteAlarmGroup, {
    onSuccess: () => {
      toast({
        title: 'Alarm group deleted.',
        description: 'The alarm group has been deleted successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setGroupToDelete(null);
      setIsConfirmDeleteModalOpen(false);
      refetchAlarmGroups();
    },
    onError: error => {
      toast({
        title: 'Error deleting alarm group.',
        description: getApiErrorMessage(
          error,
          'Failed to delete the alarm group.'
        ),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const updateGroupMutation = useMutation(updateAlarmGroup, {
    onSuccess: () => {
      toast({
        title: 'Alarm group updated.',
        description: 'The alarm group has been updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refetchAlarmGroups();
      setIsEditGroupModalOpen(false);
    },
    onError: error => {
      toast({
        title: 'Error updating alarm group.',
        description: getApiErrorMessage(
          error,
          'Failed to update the alarm group.'
        ),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    if (tagData) {
      setTags(tagData);
    }
  }, [tagData]);

  useEffect(() => {
    if (alarmGroupData) {
      setAlarmGroups(alarmGroupData);
    }
  }, [alarmGroupData]);

  const sortedAlarmData = alarmData ? [...alarmData] : [];

  const handleEditClick = async alarm => {
    setIsFetchingAlarm(true);
    try {
      const alarmDetails = await getAlarmById(alarm.id);
      setSelectedAlarm(alarmDetails);
      setNewAlarm({
        enable: alarmDetails.enable,
        name: alarmDetails.name,
        code: alarmDetails.code,
        operand: alarmDetails.operand,
        firstReferance: alarmDetails.firstReferance,
        secondReferance: alarmDetails.secondReferance,
        delay: alarmDetails.delay,
        threshold: alarmDetails.threshold,
        group: alarmDetails.group,
      });
      setIsEditModalOpen(true);
    } catch (error) {
      toast({
        title: 'Error fetching alarm details',
        description: getApiErrorMessage(
          error,
          'Failed to fetch the alarm details.'
        ),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsFetchingAlarm(false);
    }
  };

  const handleDeleteClick = alarm => {
    setAlarmToDelete(alarm);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (alarmToDelete) {
      deleteMutation.mutate(alarmToDelete.id);
    }
  };

  const handleCreateClick = () => {
    setNewAlarm({
      enable: false,
      name: '',
      group: 'warning',
      operand: 3,
      delay: 'none',
      firstReferance: '',
      secondReferance: '',
      code: '',
      threshold: '',
    });
    setIsCreateModalOpen(true);
  };

  const handleTagClick = alarm => {
    setSelectedTagAlarm(alarm); // Set the selected alarm
    setIsTagModalOpen(true); // Open the modal

    const alarmOperand = alarm.operand;

    setOperand(alarmOperand); // Set the operand for the modal
  };

  const handleSelectTag = tagId => {
    if (selectedTagAlarm) {
      tagMutation.mutate({
        alarmId: selectedTagAlarm.id,
        IdOrValue: tagId,
        ref_type: 0,
      });
    }
  };

  const handleReferenceClick = (alarmId, label, selectedValue, refType) => {
    const alarm = alarmData.find(alarm => alarm.id === alarmId);
    const alarmOperand = alarm?.operand;
    let alarmTagType = null;

    if (alarm?.tagId && tags.length > 0) {
      // `tags` listesinin içindeki tüm cihazları ve onların tag'lerini tarayarak `tagId`'yi bul
      for (const deviceGroup of tags) {
        for (const device of deviceGroup.device) {
          const foundTag = device.tag.find(tag => tag.id === alarm.tagId);
          if (foundTag) {
            alarmTagType = foundTag.type;
            break; // Tag bulunduysa döngüden çık
          }
        }
        if (alarmTagType !== null) break; // Eğer tag bulunduysa üst döngüyü de sonlandır
      }
    }

    setSelectedReferenceAlarmId(alarmId);
    setReferenceModalLabel(label);
    setSelectedReference(selectedValue);
    setRefType(refType);
    setOperand(alarmOperand);
    setIsReferenceModalOpen(true);

    // Seçili alarmı ve tagType'ı kaydet
    setSelectedTagAlarm({
      ...alarm,
      tagType: alarmTagType, // Tag'in type değeri burada
    });
  };

  const handleSaveReference = async (IdOrValue, ref_type) => {
    try {
      await updateAlarmTag({
        alarmId: selectedReferenceAlarmId,
        IdOrValue,
        ref_type,
      });
      toast({
        title: 'Reference saved.',
        description: 'The reference has been saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsReferenceModalOpen(false);
      refetchAlarms();
    } catch (error) {
      toast({
        title: 'Error saving reference.',
        description: getApiErrorMessage(error, 'Failed to save the reference.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const closeModals = () => {
    setIsEditModalOpen(false);
    setIsCreateModalOpen(false);
    setIsTagModalOpen(false);
    setIsReferenceModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsAddGroupModalOpen(false);
    setIsEditGroupModalOpen(false);
    setIsConfirmDeleteModalOpen(false);
    setSelectedAlarm(null);
    setSelectedTagAlarm(null);
    setSelectedReferenceAlarmId(null);
    setSelectedReference('');
    setAlarmToDelete(null);
    setGroupToEdit(null);
    setGroupToDelete(null);
  };

  const handleSaveNewAlarm = () => {
    mutation.mutate(newAlarm);
  };

  const handleSaveUpdatedAlarm = () => {
    if (selectedAlarm) {
      updateMutation.mutate({ id: selectedAlarm.id, alarm: newAlarm });
    }
  };

  const handleNewAlarmChange = e => {
    const { name, value, type, checked } = e.target;
    setNewAlarm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddGroupClick = () => {
    setIsAddGroupModalOpen(true);
  };

  const handleEditGroupClick = group => {
    setGroupToEdit(group);
    setEditGroup({
      id: group.id,
      name: group.name,
      description: group.description,
    });
    setIsEditGroupModalOpen(true);
  };

  const handleDeleteGroupClick = groupId => {
    setGroupToDelete(groupId);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleConfirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteGroupMutation.mutate(groupToDelete);
    }
  };

  const handleSaveUpdatedGroup = () => {
    if (groupToEdit) {
      updateGroupMutation.mutate({
        id: groupToEdit.id,
        group: editGroup,
      });
    }
  };

  const handleExportAlarms = async () => {
    try {
      const data = await exportAlarmsToExcel();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'alarms_export.xlsx');
    } catch (error) {
      console.error('Error exporting alarms:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error, 'Failed to export alarms.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (alarmLoading || tagLoading || alarmGroupLoading || isFetchingAlarm) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (alarmError || tagError || alarmGroupError) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>
          An error occurred:{' '}
          {getApiErrorMessage(alarmError || tagError || alarmGroupError)}
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <Box w="100%">
        <Flex justifyContent="space-between" alignItems="center" p={5}>
          <Heading size="md">Alarm Rules</Heading>
        </Flex>

        <TableContainer overflowX="auto">
          <Table variant="simple" colorScheme="gray" size="sm">
            <Thead>
              <Tr>
                <Th px={4} py={2}>
                  Enable
                </Th>
                <Th px={4} py={2}>
                  Name
                </Th>
                <Th px={4} py={2}>
                  Code
                </Th>
                <Th px={4} py={2}>
                  Group
                </Th>
                <Th px={4} py={2}>
                  Tag
                </Th>
                <Th px={4} py={2}>
                  Operand
                </Th>
                <Th px={4} py={2}>
                  Reference 1
                </Th>
                <Th px={4} py={2}>
                  Reference 2
                </Th>
                <Th px={4} py={2}>
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedAlarmData.map(alarm => (
                <Tr key={alarm.id}>
                  <Td px={4} py={2}>
                    {alarm.enabled?.toString().toUpperCase()}
                  </Td>
                  <Td px={4} py={2}>
                    {alarm.name}
                  </Td>
                  <Td px={4} py={2}>
                    {alarm.code}
                  </Td>
                  <Td px={4} py={2}>
                    {alarm.group}
                  </Td>
                  <Td px={4} py={2}>
                    <Text
                      onClick={() => handleTagClick(alarm)}
                      as="span"
                      textDecoration="underline"
                      cursor="pointer"
                    >
                      {alarm.tag ? alarm.tag : 'Select'}
                    </Text>
                  </Td>
                  <Td px={4} py={2}>
                    {alarm.operand}
                  </Td>
                  <Td px={4} py={2}>
                    <Text
                      onClick={() =>
                        handleReferenceClick(
                          alarm.id,
                          'Reference 1',
                          alarm.firstReferance,
                          1
                        )
                      }
                      as="span"
                      textDecoration="underline"
                      cursor="pointer"
                    >
                      {alarm.firstReferance ? alarm.firstReferance : 'Select'}
                    </Text>
                  </Td>
                  <Td px={4} py={2}>
                    {['in range', 'out of range'].includes(
                      alarm.operand?.toLowerCase()
                    ) ? (
                      <Text
                        onClick={() =>
                          handleReferenceClick(
                            alarm.id,
                            'Reference 2',
                            alarm.secondReferance,
                            2
                          )
                        }
                        as="span"
                        textDecoration="underline"
                        cursor="pointer"
                      >
                        {alarm.secondReferance
                          ? alarm.secondReferance
                          : 'Select'}
                      </Text>
                    ) : (
                      <Text as="span" color="gray.500" cursor="not-allowed">
                        N/A
                      </Text>
                    )}
                  </Td>

                  <Td px={4} py={2}>
                    <Text
                      onClick={() => handleEditClick(alarm)}
                      as="span"
                      textDecoration="underline"
                      cursor="pointer"
                    >
                      Edit
                    </Text>
                    {' | '}
                    <Text
                      onClick={() => handleDeleteClick(alarm)}
                      as="span"
                      textDecoration="underline"
                      cursor="pointer"
                    >
                      Delete
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {isEditModalOpen && (
          <CreateAlarmModalComponent
            isOpen={isEditModalOpen}
            onClose={closeModals}
            alarmGroups={alarmGroups}
            newAlarm={newAlarm}
            setNewAlarm={setNewAlarm}
            handleNewAlarmChange={handleNewAlarmChange}
            refetchAlarms={refetchAlarms}
            isEditMode={true}
            selectedAlarm={selectedAlarm}
            onSave={handleSaveUpdatedAlarm}
          />
        )}

        {isCreateModalOpen && (
          <CreateAlarmModalComponent
            isOpen={isCreateModalOpen}
            onClose={closeModals}
            onSave={handleSaveNewAlarm}
            newAlarm={newAlarm}
            setNewAlarm={setNewAlarm}
            handleNewAlarmChange={handleNewAlarmChange}
            alarmGroups={alarmGroups}
            refetchAlarms={refetchAlarms}
          />
        )}

        {isTagModalOpen && (
          <SelectTagModal
            isOpen={isTagModalOpen}
            onClose={closeModals}
            tags={tags}
            onSelectTag={handleSelectTag}
            operand={selectedTagAlarm?.operand} // Pass the correct operand here
          />
        )}

        {isReferenceModalOpen && (
          <ReferenceInputModal
            isOpen={isReferenceModalOpen}
            onClose={closeModals}
            label={referenceModalLabel}
            selectedReference={selectedReference}
            handleSaveReference={handleSaveReference}
            tags={tags}
            ref_type={refType}
            operand={operand} // Ensure this line correctly passes the operand
            tagType={selectedTagAlarm?.tagType} // Pass the tag type if available
          />
        )}

        {isDeleteModalOpen && (
          <DeleteAlarmRulesModalComponent
            isOpen={isDeleteModalOpen}
            onClose={closeModals}
            onDelete={handleDeleteConfirm}
          />
        )}

        {isAddGroupModalOpen && (
          <AddAlarmGroupModalComponent
            isOpen={isAddGroupModalOpen}
            onClose={closeModals}
            refetchAlarmGroups={refetchAlarmGroups}
            alarmGroup={{ id: '', name: '', description: '' }}
            isEditMode={false}
          />
        )}

        <Flex justifyContent="flex-end" alignItems="center" p={5} mt={4}>
          <HStack spacing={2}>
            <Button size="sm">Transfer</Button>
            <Button size="sm" onClick={handleExportAlarms}>
              Export
            </Button>
            <Button size="sm" onClick={() => setIsImportModalOpen(true)}>
              Import
            </Button>

            {/* Import Alarm Excel Modal */}
            <ImportAlarmExcelModalComponent
              isOpen={isImportModalOpen}
              onClose={() => setIsImportModalOpen(false)}
              onSuccess={refetchAlarms} // İmport sonrası alarmları güncelle
            />

            <Button size="sm" onClick={handleCreateClick}>
              Add
            </Button>
          </HStack>
        </Flex>
      </Box>
      <Box w="100%">
        <Box w="100%" mt={10}>
          <Heading size="md" mb={5}>
            Alarm Groups
          </Heading>
          <TableContainer overflowX="auto">
            <Table variant="simple" colorScheme="gray" size="sm">
              <Thead>
                <Tr>
                  <Th px={4} py={2}>
                    Name
                  </Th>
                  <Th px={4} py={2}>
                    Description
                  </Th>
                  <Th px={4} py={2}>
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {alarmGroups?.map(group => (
                  <Tr key={group.id}>
                    <Td px={4} py={2}>
                      {group.name}
                    </Td>
                    <Td px={4} py={2}>
                      {group.description || 'N/A'}
                    </Td>
                    <Td px={4} py={2}>
                      <Text
                        onClick={() => handleEditGroupClick(group)}
                        as="span"
                        textDecoration="underline"
                        cursor="pointer"
                      >
                        Edit
                      </Text>
                      {' | '}
                      <Text
                        onClick={() => handleDeleteGroupClick(group.id)}
                        as="span"
                        textDecoration="underline"
                        cursor="pointer"
                      >
                        Delete
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {isEditGroupModalOpen && (
            <AddAlarmGroupModalComponent
              isOpen={isEditGroupModalOpen}
              onClose={closeModals}
              alarmGroup={editGroup} // Düzenlenecek grup verileri
              setAlarmGroup={setEditGroup} // Grup verilerini güncelleme fonksiyonu
              onSave={handleSaveUpdatedGroup}
              refetchAlarmGroups={refetchAlarmGroups}
              isEditMode={true} // Düzenleme modu
            />
          )}

          {isConfirmDeleteModalOpen && (
            <Modal isOpen={isConfirmDeleteModalOpen} onClose={closeModals}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Delete Alarm Group</ModalHeader>
                <ModalBody>
                  <Text>
                    Are you sure you want to delete this alarm group? This
                    action cannot be undone.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" onClick={closeModals}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={handleConfirmDeleteGroup}
                    ml={3}
                  >
                    Delete
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}

          <Flex justifyContent="flex-end" alignItems="center" p={5} mt={4}>
            <HStack spacing={2}>
              <Button size="sm">Transfer</Button>
              <Button size="sm">Export</Button>
              <Button size="sm">Import</Button>
              <Button size="sm" onClick={handleAddGroupClick}>
                Add
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default Alarms;
